import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Permissions } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import PersonalLandingSectionsEditor from '../features/publicPage/PersonalLandingSectionsEditor'
import MenuGuideButton from '../components/MenuGuideButton'
import {
  LANDING_SKIN_OPTIONS,
  createLandingSectionsFromPreset,
  serializeLandingSections,
} from '../features/publicPage/registry'
import { normalizePersonalLandingSections } from '../features/personalLanding/normalize'

const TEMPLATE_GUIDES = {
  'user-landing-trust': {
    tags: ['신뢰형', '경력', '지표'],
    headline: '프로필·경력·지표 중심의 균형 잡힌 소개 구성',
    scenario: '장기 신뢰와 전문성을 먼저 보여주고 싶은 설계사에게 적합합니다.',
    operatorNote: '기본 시작점으로 쓰기 좋습니다. 필요한 섹션만 켜고 끄면 됩니다.',
  },
  'user-landing-conversion': {
    tags: ['전환형', '이달의이슈', 'CTA'],
    headline: '콘텐츠와 사례로 관심을 끌고 상담으로 연결',
    scenario: '월간 이슈·사례 노출과 빠른 문의 전환을 원할 때 적합합니다.',
    operatorNote: '이미지 카드 섹션은 사용자가 직접 이미지를 올리도록 안내하세요.',
  },
  'user-landing-brand': {
    tags: ['브랜드형', '갤러리', '스토리'],
    headline: '비주얼과 메시지로 브랜드 인상을 강화',
    scenario: '사진·스토리텔링이 강점인 설계사에게 적합합니다.',
    operatorNote: '미디어 갤러리는 사용자 업로드 기준으로 동작합니다.',
  },
}

function emptyEditorPreset() {
  return {
    landingTemplateId: 'LANDING_V1',
    landingSections: serializeLandingSections(createLandingSectionsFromPreset({}, 'trust')),
  }
}

export default function UserHomepageTemplatesPage() {
  const { show } = useToast()
  const { allow } = useAccess()
  const [q, setQ] = useState('')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [sortOrder, setSortOrder] = useState(0)
  const [metaModalOpen, setMetaModalOpen] = useState(false)
  const [landingTemplateId, setLandingTemplateId] = useState('LANDING_V1')
  const [landingSections, setLandingSections] = useState(() =>
    normalizePersonalLandingSections(createLandingSectionsFromPreset({}, 'trust'), {}))
  const [expandedSectionId, setExpandedSectionId] = useState('')

  const canManage = allow.perm([Permissions.TEMPLATE_MANAGE])

  const qRef = useRef(q)
  useEffect(() => {
    qRef.current = q
  }, [q])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const searchQ = qRef.current
      const res = await api.get('/user-landing-page-templates', { params: { q: searchQ || undefined } })
      const payload = res?.data?.data || res?.data
      setItems(Array.isArray(payload?.items) ? payload.items : [])
    } catch {
      show('개인홈페이지 템플릿을 불러오지 못했습니다', 'error')
    } finally {
      setLoading(false)
    }
  }, [show])

  useEffect(() => {
    reload()
  }, [reload])

  const filtered = useMemo(() => items, [items])
  const selectedTemplate = useMemo(
    () => (items || []).find((t) => String(t.id) === String(selectedTemplateId)),
    [items, selectedTemplateId]
  )
  const selectedGuide = useMemo(
    () => TEMPLATE_GUIDES[selectedTemplate?.code] || null,
    [selectedTemplate]
  )

  const resetForm = () => {
    setEditingId(null)
    setSelectedTemplateId('')
    setCode('')
    setName('')
    setDescription('')
    setIsActive(true)
    setSortOrder(0)
    const ep = emptyEditorPreset()
    setLandingTemplateId(ep.landingTemplateId)
    setLandingSections(normalizePersonalLandingSections(ep, {}))
    setExpandedSectionId('')
  }

  const applyPresetToEditor = (presetObj) => {
    const raw = presetObj && typeof presetObj === 'object' ? presetObj : {}
    setLandingTemplateId(typeof raw.landingTemplateId === 'string' && raw.landingTemplateId ? raw.landingTemplateId : 'LANDING_V1')
    setLandingSections(normalizePersonalLandingSections(raw, {}))
    setExpandedSectionId('')
  }

  const onEdit = (item) => {
    setSelectedTemplateId(String(item.id))
    setEditingId(item.id)
    setCode(item.code || '')
    setName(item.name || '')
    setDescription(item.description || '')
    setIsActive(item.isActive !== false)
    setSortOrder(Number(item.sortOrder || 0))
    applyPresetToEditor(item.preset)
  }

  const buildPresetPayload = () => ({
    landingTemplateId,
    landingSections: serializeLandingSections(landingSections),
  })

  const saveTemplate = async () => {
    if (!canManage) {
      show('권한이 없습니다', 'error')
      return
    }
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    const hh = String(now.getHours()).padStart(2, '0')
    const mm = String(now.getMinutes()).padStart(2, '0')
    const ss = String(now.getSeconds()).padStart(2, '0')
    const autoSuffix = `${y}${m}${d}${hh}${mm}${ss}`
    const normalizedCode = (code || '').trim() || `user-template-${autoSuffix}`
    const normalizedName = (name || '').trim() || `신규 개인홈 템플릿 ${y}-${m}-${d}`
    setSaving(true)
    try {
      const body = {
        code: normalizedCode,
        name: normalizedName,
        description,
        preset: buildPresetPayload(),
        isActive,
        sortOrder: Number(sortOrder || 0),
      }
      if (editingId) {
        await api.put(`/user-landing-page-templates/${editingId}`, body)
        show('템플릿이 수정되었습니다', 'success')
      } else {
        await api.post('/user-landing-page-templates', body)
        show('템플릿이 생성되었습니다', 'success')
        setCode(normalizedCode)
        setName(normalizedName)
      }
      setMetaModalOpen(false)
      resetForm()
      await reload()
    } catch (e2) {
      show('템플릿 저장 실패: ' + (e2?.response?.data?.message || e2?.message || '알 수 없는 오류'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async (id) => {
    if (!canManage) return
    if (!window.confirm('정말 삭제하시겠습니까?')) return
    try {
      await api.delete(`/user-landing-page-templates/${id}`)
      show('삭제되었습니다', 'success')
      await reload()
    } catch {
      show('삭제 실패', 'error')
    }
  }

  const onClone = (item) => {
    setEditingId(null)
    setSelectedTemplateId('')
    setCode(`${item.code || 'template'}-copy`)
    setName(`${item.name || '템플릿'} (복제본)`)
    setDescription(item.description || '')
    setIsActive(item.isActive !== false)
    setSortOrder(Number(item.sortOrder || 0) + 1)
    applyPresetToEditor(item.preset)
    show('복제본으로 불러왔습니다. 코드/이름을 확인 후 저장하세요.', 'success')
    setMetaModalOpen(true)
  }

  const onTemplateChange = (id) => {
    setSelectedTemplateId(id)
    const item = (items || []).find((t) => String(t.id) === String(id))
    if (!item) return
    onEdit(item)
  }

  const updateSectionContent = (index, updater) => {
    setLandingSections((prev) => prev.map((section, i) => {
      if (i !== index) return section
      const nextContent = typeof updater === 'function'
        ? updater(section?.content || {})
        : updater
      return { ...section, content: nextContent && typeof nextContent === 'object' ? nextContent : {} }
    }))
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">템플릿관리 &gt; 개인홈페이지</h1>
          <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <select
          className="w-full md:w-72 rounded border px-3 py-2 text-sm"
          value={selectedTemplateId}
          onChange={(e) => onTemplateChange(e.target.value)}
        >
          <option value="">템플릿 선택</option>
          {filtered.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.code})
            </option>
          ))}
        </select>
        <input
          className="w-full md:w-96 rounded border px-3 py-2 text-sm"
          placeholder="검색: 코드/이름"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" className="rounded border px-3 py-2 text-sm" onClick={reload}>검색</button>
        <button type="button" className="rounded border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100" onClick={resetForm}>새 템플릿</button>
        <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setMetaModalOpen(true)}>메타정보</button>
        {selectedTemplate && (
          <button type="button" className="rounded border px-3 py-2 text-sm text-indigo-700" onClick={() => onClone(selectedTemplate)}>복제</button>
        )}
        {selectedTemplate && (
          <button type="button" className="rounded border px-3 py-2 text-sm text-red-700" onClick={() => onDelete(selectedTemplate.id)}>삭제</button>
        )}
        <button
          type="button"
          className="rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={!canManage || saving}
          onClick={() => (editingId ? saveTemplate() : setMetaModalOpen(true))}
        >
          {saving ? '저장 중…' : '프리셋 저장'}
        </button>
      </div>

      {loading ? <div className="text-sm text-slate-500">불러오는 중…</div> : null}

      {selectedTemplate ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">{selectedTemplate.name || '템플릿'}</span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{selectedTemplate.code}</span>
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${selectedTemplate.isActive !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              {selectedTemplate.isActive !== false ? '활성' : '비활성'}
            </span>
          </div>
          {selectedGuide?.headline ? (
            <div className="text-sm text-slate-700">{selectedGuide.headline}</div>
          ) : null}
          <div className="text-xs text-slate-400">
            마지막 수정 {selectedTemplate.updatedAt ? formatDateTimeMinute(selectedTemplate.updatedAt) : '-'}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">페이지 스킨(기본값)</span>
          <select
            className="rounded border px-2 py-1.5 text-sm"
            value={landingTemplateId}
            onChange={(e) => setLandingTemplateId(e.target.value)}
          >
            {LANDING_SKIN_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>
        <PersonalLandingSectionsEditor
          mode="preset"
          landingSections={landingSections}
          setLandingSections={setLandingSections}
          expandedSectionId={expandedSectionId}
          setExpandedSectionId={setExpandedSectionId}
          updateSectionContent={updateSectionContent}
          presetData={{}}
        />
      </div>

      {metaModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-lg bg-white border shadow-lg p-4 space-y-3">
            <h3 className="text-lg font-semibold">{editingId ? '템플릿 메타정보 수정' : '템플릿 메타정보 생성'}</h3>
            <input className="w-full rounded border px-3 py-2" placeholder="코드 (예: user-landing-custom)" value={code} onChange={(e) => setCode(e.target.value)} />
            <input className="w-full rounded border px-3 py-2" placeholder="템플릿명" value={name} onChange={(e) => setName(e.target.value)} />
            <textarea className="w-full rounded border px-3 py-2" rows={2} placeholder="설명" value={description} onChange={(e) => setDescription(e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                활성
              </label>
              <input className="w-full rounded border px-3 py-2" type="number" placeholder="정렬순서" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded border px-3 py-2" onClick={() => setMetaModalOpen(false)}>닫기</button>
              <button
                type="button"
                disabled={!canManage || saving}
                className="rounded bg-brand-navy text-white px-3 py-2 disabled:opacity-50"
                onClick={saveTemplate}
              >
                {saving ? '저장 중...' : editingId ? '수정 저장' : '생성 저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
